import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import MediaUpload from './MediaUpload';
import { 
  Save, 
  Store, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock,
  Calendar,
  Plus,
  Trash2,
  Edit,
  AlertCircle,
  Info,
  Sparkles,
  Eye,
  EyeOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopSettingsEnhanced = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  const [settings, setSettings] = useState({
    shop_name: '',
    address_street: '',
    address_zip: '',
    address_city: '',
    address_country: '',
    phone_mobile: '',
    phone_fixed: '',
    email: '',
    website: '',
    vat_number: '',
    business_hours: {
      monday: '9h00 - 18h00',
      tuesday: '9h00 - 18h00',
      wednesday: '9h00 - 18h00',
      thursday: '9h00 - 18h00',
      friday: '9h00 - 18h00',
      saturday: '9h00 - 13h00',
      sunday: 'Fermé'
    },
    exceptional_hours: [],
    flash_info: []
  });

  const [showExceptionalDialog, setShowExceptionalDialog] = useState(false);
  const [showFlashInfoDialog, setShowFlashInfoDialog] = useState(false);
  const [editingExceptional, setEditingExceptional] = useState(null);
  const [editingFlashInfo, setEditingFlashInfo] = useState(null);

  const [newExceptional, setNewExceptional] = useState({
    date: '',
    description: '',
    hours: ''
  });

  const [newFlashInfo, setNewFlashInfo] = useState({
    title: '',
    message: '',
    type: 'info',
    is_active: true,
    show_on_homepage: true,
    show_on_tracking: false,
    show_on_appointments: false,
    show_on_promotions: false,
    expires_at: '',
    media: { type: 'none', url: null }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/shop-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBusinessHoursChange = (day, value) => {
    setSettings(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: value
      }
    }));
  };

  const handleAddExceptional = () => {
    if (!newExceptional.date || !newExceptional.description || !newExceptional.hours) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const exceptional = {
      id: Date.now().toString(),
      ...newExceptional
    };

    setSettings(prev => ({
      ...prev,
      exceptional_hours: [...prev.exceptional_hours, exceptional]
    }));

    setNewExceptional({ date: '', description: '', hours: '' });
    setShowExceptionalDialog(false);
    toast.success('Horaire exceptionnel ajouté');
  };

  const handleDeleteExceptional = (id) => {
    setSettings(prev => ({
      ...prev,
      exceptional_hours: prev.exceptional_hours.filter(h => h.id !== id)
    }));
    toast.success('Horaire exceptionnel supprimé');
  };

  const handleAddFlashInfo = () => {
    if (!newFlashInfo.title || !newFlashInfo.message) {
      toast.error('Veuillez remplir le titre et le message');
      return;
    }

    const flashInfo = {
      id: Date.now().toString(),
      ...newFlashInfo,
      created_at: new Date().toISOString()
    };

    setSettings(prev => ({
      ...prev,
      flash_info: [...prev.flash_info, flashInfo]
    }));

    setNewFlashInfo({
      title: '',
      message: '',
      type: 'info',
      is_active: true,
      show_on_homepage: true,
      show_on_tracking: false,
      show_on_appointments: false,
      show_on_promotions: false,
      expires_at: '',
    media: { type: 'none', url: null }
    });
    setShowFlashInfoDialog(false);
    toast.success('Flash info ajouté');
  };

  const handleToggleFlashInfo = (id) => {
    setSettings(prev => ({
      ...prev,
      flash_info: prev.flash_info.map(info =>
        info.id === id ? { ...info, is_active: !info.is_active } : info
      )
    }));
  };

  const handleDeleteFlashInfo = (id) => {
    setSettings(prev => ({
      ...prev,
      flash_info: prev.flash_info.filter(info => info.id !== id)
    }));
    toast.success('Flash info supprimé');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/shop-settings`, settings);
      toast.success('Paramètres enregistrés avec succès');
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas les droits pour modifier les paramètres');
      } else {
        toast.error('Erreur lors de l\'enregistrement des paramètres');
      }
    } finally {
      setSaving(false);
    }
  };

  const getFlashInfoIcon = (type) => {
    switch (type) {
      case 'urgent':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'promotion':
        return <Sparkles className="w-4 h-4 text-green-600" />;
      default:
        return <Info className="w-4 h-4 text-blue-600" />;
    }
  };

  const getFlashInfoBadge = (type) => {
    const badges = {
      urgent: { label: 'Urgent', className: 'bg-red-100 text-red-800 border-red-200' },
      promotion: { label: 'Promotion', className: 'bg-green-100 text-green-800 border-green-200' },
      info: { label: 'Info', className: 'bg-blue-100 text-blue-800 border-blue-200' }
    };
    const badge = badges[type] || badges.info;
    return <Badge className={`${badge.className} border`}>{badge.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const days = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' },
    { key: 'saturday', label: 'Samedi' },
    { key: 'sunday', label: 'Dimanche' }
  ];

  return (
    <div className="space-y-6 fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Paramètres du magasin</h1>
          <p className="text-slate-600 mt-1">Gérer les informations, horaires et annonces</p>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
        >
          {saving ? (
            <>
              <div className="spinner w-4 h-4 mr-2"></div>
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </>
          )}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="info">
            <Store className="w-4 h-4 mr-2" />
            Informations
          </TabsTrigger>
          <TabsTrigger value="hours">
            <Clock className="w-4 h-4 mr-2" />
            Horaires
          </TabsTrigger>
          <TabsTrigger value="flash">
            <AlertCircle className="w-4 h-4 mr-2" />
            Flash Info
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Shop Information */}
        <TabsContent value="info" className="space-y-6">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Informations du magasin</CardTitle>
              <CardDescription>Coordonnées et informations de contact</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shop_name">Nom du magasin</Label>
                  <Input
                    id="shop_name"
                    name="shop_name"
                    value={settings.shop_name}
                    onChange={handleChange}
                    placeholder="Smart Service Mobile"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vat_number">Numéro TVA</Label>
                  <Input
                    id="vat_number"
                    name="vat_number"
                    value={settings.vat_number || ''}
                    onChange={handleChange}
                    placeholder="BE0123456789"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_street">Adresse</Label>
                <Input
                  id="address_street"
                  name="address_street"
                  value={settings.address_street}
                  onChange={handleChange}
                  placeholder="Rue de la Réparation 123"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address_zip">Code postal</Label>
                  <Input
                    id="address_zip"
                    name="address_zip"
                    value={settings.address_zip}
                    onChange={handleChange}
                    placeholder="4000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address_city">Ville</Label>
                  <Input
                    id="address_city"
                    name="address_city"
                    value={settings.address_city}
                    onChange={handleChange}
                    placeholder="Liège"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address_country">Pays</Label>
                  <Input
                    id="address_country"
                    name="address_country"
                    value={settings.address_country}
                    onChange={handleChange}
                    placeholder="Belgique"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone_mobile">Téléphone mobile</Label>
                  <Input
                    id="phone_mobile"
                    name="phone_mobile"
                    value={settings.phone_mobile}
                    onChange={handleChange}
                    placeholder="+32 4 XX XX XX XX"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone_fixed">Téléphone fixe</Label>
                  <Input
                    id="phone_fixed"
                    name="phone_fixed"
                    value={settings.phone_fixed || ''}
                    onChange={handleChange}
                    placeholder="+32 4 XX XX XX XX"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={settings.email}
                    onChange={handleChange}
                    placeholder="contact@smartservicemobile.be"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Site web</Label>
                  <Input
                    id="website"
                    name="website"
                    value={settings.website || ''}
                    onChange={handleChange}
                    placeholder="www.smartservicemobile.be"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: Business Hours */}
        <TabsContent value="hours" className="space-y-6">
          {/* Regular Hours */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Horaires d'ouverture
              </CardTitle>
              <CardDescription>Horaires d'ouverture habituels du magasin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {days.map(day => (
                <div key={day.key} className="grid grid-cols-3 gap-4 items-center">
                  <Label className="font-medium">{day.label}</Label>
                  <Input
                    value={settings.business_hours[day.key]}
                    onChange={(e) => handleBusinessHoursChange(day.key, e.target.value)}
                    placeholder="9h00 - 18h00 ou Fermé"
                    className="col-span-2"
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Exceptional Hours */}
          <Card className="glass">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Horaires exceptionnels
                  </CardTitle>
                  <CardDescription>Jours fériés, fermetures spéciales, etc.</CardDescription>
                </div>
                <Dialog open={showExceptionalDialog} onOpenChange={setShowExceptionalDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Ajouter un horaire exceptionnel</DialogTitle>
                      <DialogDescription>
                        Définir des horaires spéciaux pour une date particulière
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={newExceptional.date}
                          onChange={(e) => setNewExceptional(prev => ({ ...prev, date: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={newExceptional.description}
                          onChange={(e) => setNewExceptional(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Ex: Noël, Congés annuels"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Horaires</Label>
                        <Input
                          value={newExceptional.hours}
                          onChange={(e) => setNewExceptional(prev => ({ ...prev, hours: e.target.value }))}
                          placeholder="Ex: Fermé ou 10h00 - 15h00"
                        />
                      </div>
                      <Button onClick={handleAddExceptional} className="w-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {settings.exceptional_hours && settings.exceptional_hours.length > 0 ? (
                <div className="space-y-2">
                  {settings.exceptional_hours.map((exceptional) => (
                    <div
                      key={exceptional.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{exceptional.description}</div>
                        <div className="text-sm text-slate-600">
                          {new Date(exceptional.date).toLocaleDateString('fr-FR')} - {exceptional.hours}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteExceptional(exceptional.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">Aucun horaire exceptionnel défini</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: Flash Info */}
        <TabsContent value="flash" className="space-y-6">
          <Card className="glass">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Flash Info & Annonces
                  </CardTitle>
                  <CardDescription>
                    Messages d'annonce pour vos clients (fermetures, promotions, etc.)
                  </CardDescription>
                </div>
                <Dialog open={showFlashInfoDialog} onOpenChange={setShowFlashInfoDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Nouveau message
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Créer un flash info</DialogTitle>
                      <DialogDescription>
                        Créer un message d'annonce pour vos clients
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Type de message</Label>
                        <Select
                          value={newFlashInfo.type}
                          onValueChange={(value) => setNewFlashInfo(prev => ({ ...prev, type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="urgent">🔴 Urgent / Fermeture</SelectItem>
                            <SelectItem value="info">ℹ️ Information</SelectItem>
                            <SelectItem value="promotion">✨ Promotion</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Titre</Label>
                        <Input
                          value={newFlashInfo.title}
                          onChange={(e) => setNewFlashInfo(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Ex: Fermé pour congés annuels"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Message</Label>
                        <Textarea
                          value={newFlashInfo.message}
                          onChange={(e) => setNewFlashInfo(prev => ({ ...prev, message: e.target.value }))}
                          placeholder="Ex: Le magasin sera fermé du 24 décembre au 2 janvier. Réouverture le 3 janvier."
                          rows={3}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Date d'expiration (optionnel)</Label>
                        <Input
                          type="datetime-local"
                          value={newFlashInfo.expires_at}
                          onChange={(e) => setNewFlashInfo(prev => ({ ...prev, expires_at: e.target.value }))}
                        />
                      </div>

                      <div className="space-y-3">
                        <Label>Afficher sur :</Label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={newFlashInfo.show_on_homepage}
                              onCheckedChange={(checked) => setNewFlashInfo(prev => ({ ...prev, show_on_homepage: checked }))}
                            />
                            <Label>Page d'accueil</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={newFlashInfo.show_on_tracking}
                              onCheckedChange={(checked) => setNewFlashInfo(prev => ({ ...prev, show_on_tracking: checked }))}
                            />
                            <Label>Page de suivi</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={newFlashInfo.show_on_appointments}
                              onCheckedChange={(checked) => setNewFlashInfo(prev => ({ ...prev, show_on_appointments: checked }))}
                            />
                            <Label>Page de rendez-vous</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={newFlashInfo.show_on_promotions}
                              onCheckedChange={(checked) => setNewFlashInfo(prev => ({ ...prev, show_on_promotions: checked }))}
                            />
                            <Label>Page promotions</Label>
                          </div>
                        </div>
                      </div>

                      <Button onClick={handleAddFlashInfo} className="w-full">
                        <Plus className="w-4 h-4 mr-2" />

                      <div>
                        <Label>Média (optionnel)</Label>
                        <MediaUpload
                          currentMedia={newFlashInfo.media}
                          onMediaChange={(media) => setNewFlashInfo(prev => ({ ...prev, media }))}
                        />
                      </div>

                        Créer le message
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {settings.flash_info && settings.flash_info.length > 0 ? (
                <div className="space-y-3">
                  {settings.flash_info.map((info) => (
                    <Card key={info.id} className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              {getFlashInfoIcon(info.type)}
                              <h3 className="font-semibold">{info.title}</h3>
                              {getFlashInfoBadge(info.type)}
                              {info.is_active ? (
                                <Badge className="bg-green-100 text-green-800 border-green-200 border">
                                  <Eye className="w-3 h-3 mr-1" />
                                  Actif
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 border-gray-200 border">
                                  <EyeOff className="w-3 h-3 mr-1" />
                                  Inactif
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-700 mb-2">{info.message}</p>
                            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                              {info.show_on_homepage && <span>• Page d'accueil</span>}
                              {info.show_on_tracking && <span>• Suivi</span>}
                              {info.show_on_appointments && <span>• Rendez-vous</span>}
                              {info.show_on_promotions && <span>• Promotions</span>}
                            </div>
                            {info.expires_at && (
                              <p className="text-xs text-slate-500 mt-1">
                                Expire le {new Date(info.expires_at).toLocaleString('fr-FR')}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleFlashInfo(info.id)}
                            >
                              {info.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteFlashInfo(info.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun flash info créé</p>
                  <p className="text-sm text-slate-400">Créez votre premier message d'annonce</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ShopSettingsEnhanced;
